import { useMemo } from 'react'
import type { FamilyState, Member } from '@domain/state.ts'
import { taskDayOf, tasksOf } from '@domain/state.ts'
import { cursoPorEdad } from '@domain/rules/mates.ts'
import { es } from '../strings/es.ts'

/**
 * La sección de juegos.
 *
 * Se entra siempre, sin permiso y sin límite: practicar nunca se raciona. Lo que
 * está racionado es lo que paga, y eso lo decide una misión — si el padre ha
 * puesto el repaso como misión del día, esta pantalla dice cuánto da y, cuando ya
 * está hecha, lo dice también en vez de dejar que el niño lo descubra al final.
 */
export function Juegos({
  member,
  state,
  day,
  onMates,
  onBack,
}: {
  member: Member
  state: FamilyState
  day: string
  onMates: () => void
  onBack: () => void
}) {
  const edad =
    member.birthYear === undefined ? undefined : new Date().getFullYear() - member.birthYear
  const curso = useMemo(() => cursoPorEdad(edad), [edad])
  const palabra = member.coinWord ?? es.today.coins

  const mision = tasksOf(state, member.id, 'mission').find((task) => task.game === 'mates')
  const hecha =
    mision !== undefined &&
    taskDayOf(state, day as never, member.id, mision.id).status === 'validated'

  return (
    <div className="mx-auto max-w-2xl px-5 pt-6 pb-16">
      <h1 className="mt-0 mb-1 text-3xl font-extrabold">{es.juegos.title}</h1>
      <p className="text-muted mt-0 mb-6">{es.juegos.explain}</p>

      <button
        type="button"
        onClick={onMates}
        className="border-rule bg-surface flex w-full items-center gap-4 rounded-xl border p-5 text-left"
      >
        <span aria-hidden="true" className="text-4xl">
          ✖️
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-xl font-bold">{es.juegos.mates}</span>
          <span className="text-muted mt-1 block text-sm">{es.juegos.matesExplain}</span>
          <span className="mt-1 block text-sm font-bold">{es.juegos.hasta(curso)}</span>
          {mision === undefined ? null : hecha ? (
            <span className="text-mint mt-2 block text-sm font-bold">{es.juegos.misionHecha}</span>
          ) : (
            <span className="text-teal mt-2 block text-sm font-bold">
              {es.juegos.mision(mision.rewards[mision.rung].coins, palabra)}
            </span>
          )}
        </span>
      </button>

      <button
        type="button"
        className="border-rule mt-8 min-h-14 w-full rounded-xl border px-6 font-bold"
        onClick={onBack}
      >
        {es.juegos.back}
      </button>
    </div>
  )
}
